/**
 * Enhanced authentication service that integrates Firebase Auth with cookie-based auth
 * This provides an additional layer of security and improves user experience
 */

import { User as FirebaseUser } from "firebase/auth";
import { auth, db } from "./firebase";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { 
  setAuthToken, 
  getAuthToken, 
  clearAuthToken, 
  setRefreshToken, 
  getRefreshToken, 
  clearAllAuthCookies,
  setUserPrefs
} from "@/utils/cookieUtils";

/**
 * Set authentication cookies based on Firebase user
 * @param user - Firebase user object
 */
export async function setAuthCookies(user: FirebaseUser): Promise<void> {
  try {
    // Get the Firebase ID token
    const idToken = await user.getIdToken();
    
    // Set the auth token cookie (short-lived)
    setAuthToken(idToken, 1); // 1 day
    
    // Store refresh token (longer-lived)
    const refreshToken = user.refreshToken;
    setRefreshToken(refreshToken, 30); // 30 days
    
    // Also store some basic user info in preferences
    setUserPrefs({
      uid: user.uid,
      email: user.email,
      name: user.displayName,
      lastLogin: new Date().toISOString()
    });
    
    // Update user's last login time in Firestore
    const userRef = doc(db, "users", user.uid);
    await updateDoc(userRef, {
      lastLogin: serverTimestamp(),
      lastActive: serverTimestamp()
    });
  } catch (error) {
    console.error("Error setting auth cookies:", error);
    // If cookies fail, we still have Firebase auth
  }
}

/**
 * Clear all authentication cookies when logging out
 */
export function clearAuthCookies(): void {
  clearAllAuthCookies();
}

/**
 * Verify and refresh the authentication token if needed
 * @returns Whether the token was successfully refreshed
 */
export async function verifyAndRefreshToken(): Promise<boolean> {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      clearAllAuthCookies();
      return false;
    }
    
    // Check if token exists and is still valid
    const token = getAuthToken();
    if (!token) {
      // No token, but user is logged in - refresh token
      await setAuthCookies(currentUser);
      return true;
    }
    
    // Alternatively, refresh token if it's close to expiry
    // This is a simplification - in a real app, you'd decode the JWT and check its expiry
    const tokenAge = Math.floor(Math.random() * 100); // Simulated check
    if (tokenAge > 70) { // If token is "old" (random simulation)
      await setAuthCookies(currentUser);
    }
    
    return true;
  } catch (error) {
    console.error("Error verifying/refreshing token:", error);
    return false;
  }
}

/**
 * Check if a user is authenticated via cookies
 * This can be used as a quick check before Firebase auth completes
 * @returns Whether valid auth cookies exist
 */
export function hasValidAuthCookies(): boolean {
  const authToken = getAuthToken();
  const refreshToken = getRefreshToken();
  
  return !!authToken && !!refreshToken;
}

/**
 * Get basic user information from cookies
 * @returns Basic user info or null if not available
 */
export function getUserInfoFromCookies(): { uid: string; email: string | null } | null {
  // This is a simplified implementation
  const token = getAuthToken();
  if (!token) return null;
  
  // In a real implementation, you would decode the JWT to get user info
  // For now, we'll return a placeholder value
  return {
    uid: "unknown",
    email: null
  };
} 